import Modal from "../../UIelements/Modal";
import { useIntl } from 'react-intl';
import { FormEvent, useCallback, useRef, useState } from "react";
import * as Styled from './styles';
import Loading from "../../Loading";
import PrimaryButton from "../../FormElements/PrimaryButton";
import InputForwardRef, { InputForwardType } from "../../FormElements/InputForwardRef";
import { VALIDATOR_REQUIRE, VALIDATOR_SERIAL_NUMBER } from "../../../utils/validators";
import { createSerialNumbers } from "../../../services/serialNumber";
import { toast } from 'react-toastify';
import { InputsType } from "../../../hooks/form-hook-typing";
import { useForm } from "../../../hooks/form-hook";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { CreateSerialNumbersRequestType } from "../../../models/serial-numbers";

enum InputSN {
  SERIAL_NUMBER = 'serialNumber',
};

const initialValue = {
  value: '',
  isValid: false,
};

const initialSNInputs: InputsType = {
  [InputSN.SERIAL_NUMBER]: initialValue,
};

const getSubmitData = (
  inputs: InputsType,
  companyId: string,
): CreateSerialNumbersRequestType => ({
  serialNumbers: inputs['serialNumber'].value,
  companyIdRequest: companyId
});

type ModalProps = {
  idCompany: string
  onCancel: () => void;
  refresh: () => void
};

const ModalAddSerialNumber = (props: ModalProps) => {
  const intl = useIntl();
  const globalUser = useSelector((state: RootState) => state.user.object);
  const [isLoading, setIsLoading] = useState(false);
  const [formUserState, inputSNHandler] = useForm(
    initialSNInputs,
    false
  );

  const serialNumberRef = useRef<InputForwardType>(null);

  const touchAllUserFields = () => {
    if (serialNumberRef && serialNumberRef.current) serialNumberRef.current.touchHandler();
  };

  const handleAddSerialNumber = useCallback(
    async (): Promise<boolean> => {
      const data = getSubmitData(
        formUserState.inputs,
        props.idCompany
      );

      setIsLoading(true);

      try {
        await createSerialNumbers(data, globalUser?.user.accessToken ?? '');

        toast.success(
          intl.formatMessage({
            id: 'company.Números de série adicionados com sucesso!',
          }).toUpperCase()
        );
        setIsLoading(false);
        return true;

      } catch (error: any) {
        console.error(error);
        setIsLoading(false);

        if (error.response && error.response.data && error.response.data.error) {
          
          const errorCode = error.response.data.error;

          if (errorCode === 'SERIAL_ALREADY_PROVISIONED') {
            toast.error(
              intl.formatMessage({ id: 'company.Um ou mais números de série já estão provisionados para outra companhia' }).toUpperCase()
            );
          } else if (errorCode === 'SERIAL_ALREADY_CONNECTED') {
            toast.error(
              intl.formatMessage({ id: 'company.Um ou mais números de série já estão conectados na conta de outra companhia' }).toUpperCase()
            );
          } else {
            toast.error(
              intl.formatMessage({ id: 'company.Não foi possível adicionar os números de série. Tente novamente mais tarde' }).toUpperCase()
            );
          }

        } else {
          toast.error(
            intl.formatMessage({ id: 'company.Não foi possível adicionar os números de série. Tente novamente mais tarde' }).toUpperCase()
          );
        }
        
        return false;
      }
    },
    [props, formUserState.inputs, globalUser, intl]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      touchAllUserFields();

      if (!formUserState.isValid || !(/^[A-Za-z0-9; ]+$/.test(formUserState.inputs.serialNumber.value))) {
        return;
      }
      
      const isSuccess = await handleAddSerialNumber();

      if (!isSuccess) return;
      
      props.onCancel();
      props.refresh();
    },
    [
      formUserState.isValid,
      formUserState.inputs,
      handleAddSerialNumber,
      props
    ]
  );

  const loading = <Loading />;

  const footer = (
    <>
      <PrimaryButton
        type='submit'
        size='buttonModal'
        model='confirmation-gradient-modal'
        width='108px'
      >
        {intl.formatMessage({ id: 'company.adicionar' })}
      </PrimaryButton>
      <PrimaryButton
        type='button'
        size='buttonModal'
        model='cancellation'
        width='108px'
        onClick={props.onCancel}
      >
        {intl.formatMessage({ id: 'menus.CANCELAR' })}
      </PrimaryButton>
    </>
  );

  const content = (
    <Styled.Container>
      <Styled.Row>
        <Styled.Content flexGrow={1}>
          <p>{intl.formatMessage({ id: 'company.Informe o número de série do equipamento que deseja adicionar abaixo.' })}</p>
          <p className='margin'>{intl.formatMessage({ id: 'company.Para incluir vários equipamentos, separe-os com ponto e vírgula (;).' })}</p>
          <InputForwardRef
            id={InputSN.SERIAL_NUMBER}
            ref={serialNumberRef}
            type='string'
            label={intl.formatMessage({ id: 'company.Número de série' }) + ' *'}
            validators={[VALIDATOR_REQUIRE()]}
            error3={Boolean(!(/^[A-Za-z0-9; ]+$/.test(formUserState.inputs.serialNumber.value)) && formUserState.inputs.serialNumber.value.length)}
            placeholder={intl.formatMessage({ id: 'company.Informe os números de série' })}
            errorText={intl.formatMessage({
              id: 'company.Preencha pelo menos um número de série',
            })}
            errorText3={intl.formatMessage({
              id: `company.Caracteres especiais não são permitidos, exceto o ponto e vírgula (';')`
            })}
            onInput={inputSNHandler}
          />
        </Styled.Content>
      </Styled.Row>
    </Styled.Container>
  );

  return (
    <>
      <Modal
        title={intl.formatMessage({ id: 'company.ADICIONAR EQUIPAMENTOS' })}
        width='500px'
        children={isLoading ? loading : content}
        footer={footer}
        onCancel={props.onCancel}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default ModalAddSerialNumber;